/**
 * ProjectDialog - Universal Create/Edit Project Dialog
 * 
 * Mode determined by whether `project` prop is provided.
 */

import React, { useState } from 'react';

interface ProjectDialogProps {
  project?: {
    id: number;
    name: string;
    objective?: string;
    project_type?: string;
    domain?: string;
    status: string;
  } | null;
  onSave: (data: {
    name: string;
    objective?: string;
    project_type?: string;
    domain?: string;
    status?: string;
  }) => void;
  onClose: () => void;
}

const PROJECT_TYPES = [
  { value: '', label: 'Select type...' },
  { value: 'product_build', label: 'Product Build' },
  { value: 'transformation', label: 'Business Transformation' },
  { value: 'team_expansion', label: 'Team Expansion' },
  { value: 'venture_innovation', label: 'Venture / Innovation' },
  { value: 'campaign', label: 'Marketing Campaign' },
  { value: 'migration', label: 'System Migration' },
  { value: 'research', label: 'Research / PoC' },
];

const DOMAINS = [
  { value: '', label: 'Select domain...' },
  { value: 'data_ai', label: 'Data & AI' },
  { value: 'frontend_platform', label: 'Frontend Platform' },
  { value: 'backend_platform', label: 'Backend Platform' },
  { value: 'product_development', label: 'Product Development' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'business_transformation', label: 'Business Transformation' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'venture_innovation', label: 'Venture & Innovation' },
  { value: 'sales', label: 'Sales & BD' },
];

const STATUSES = [
  { value: 'planned', label: 'Planned' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

export const ProjectDialog: React.FC<ProjectDialogProps> = ({
  project,
  onSave,
  onClose,
}) => {
  const isEditMode = !!project;

  const [name, setName] = useState(project?.name || '');
  const [objective, setObjective] = useState(project?.objective || '');
  const [projectType, setProjectType] = useState(project?.project_type || '');
  const [domain, setDomain] = useState(project?.domain || '');
  const [status, setStatus] = useState(project?.status || 'planned');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Project name is required');
      return;
    }

    onSave({
      name: trimmedName,
      objective: objective.trim() || undefined,
      project_type: projectType || undefined,
      domain: domain || undefined,
      status,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="w-[440px] bg-slate-800 border border-gray-700 rounded-xl shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-white font-bold text-lg">
            {isEditMode ? `Edit Project: ${project!.name}` : 'Create New Project'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-lg">x</button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Project Name */}
          <div>
            <label className="block text-gray-400 text-xs mb-1">Project Name *</label>
            <input
              type="text"
              value={name}
              onChange={e => { setName(e.target.value); setError(''); }}
              placeholder="Enter project name..."
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none focus:border-indigo-500 transition-colors"
              autoFocus
            />
          </div>

          {/* Objective */}
          <div>
            <label className="block text-gray-400 text-xs mb-1">Objective</label>
            <textarea
              value={objective}
              onChange={e => setObjective(e.target.value)}
              placeholder="What is the high-level goal?"
              rows={2}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none resize-none focus:border-indigo-500 transition-colors"
            />
          </div>

          {/* Domain + Type (side by side) */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-gray-400 text-xs mb-1">Domain</label>
              <select
                value={domain}
                onChange={e => setDomain(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none focus:border-indigo-500 transition-colors"
              >
                {DOMAINS.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-gray-400 text-xs mb-1">Project Type</label>
              <select
                value={projectType}
                onChange={e => setProjectType(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none focus:border-indigo-500 transition-colors"
              >
                {PROJECT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-gray-400 text-xs mb-1">Status</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none focus:border-indigo-500 transition-colors"
            >
              {STATUSES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* Error */}
          {error && (
            <p className="text-red-400 text-xs">{error}</p>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {isEditMode ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
